<template>
    <div class="note-row">
        <div class="note-avatar">{{ note.authorInitials }}</div>
        <div class="note-content">
            <div class="note-meta">
                <span class="note-author">{{ note.authorName }}</span>
                <span class="note-timestamp">{{ formatNoteTimestamp(note.createdAt) }}</span>
                <span class="note-category-pill" :style="{ backgroundColor: pillColor.background, color: pillColor.color }">
                    {{ categoryLabel(note.category, note.categoryLabel) }}
                </span>
            </div>
            <!-- Body is rendered as escaped plain text. Text interpolation escapes any
                 HTML/script; white-space: pre-wrap preserves the author's line breaks. -->
            <p class="note-body">{{ note.body }}</p>
        </div>
        <slot name="actions" />
    </div>
</template>

<script setup lang="ts">
import type { SupportNoteV1 } from '@/api/supportNotes'
import { categoryColor, categoryLabel } from './categories'
import { formatNoteTimestamp } from '@/lib/supportNotesTime'
import { computed } from 'vue'

const props = defineProps<{ note: SupportNoteV1 }>()

const pillColor = computed(() => categoryColor(props.note.category))
</script>

<style lang="scss" scoped>
.note-row {
    display: flex;
    align-items: flex-start;
    gap: 12px;
    padding: 12px 0;
    border-top: 1px solid rgb(210, 219, 224);
}

.note-avatar {
    flex: 0 0 auto;
    width: 32px;
    height: 32px;
    border-radius: 50%;
    background-color: rgb(223, 240, 252);
    color: rgb(35, 94, 150);
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 12px;
    font-weight: 700;
}

.note-content {
    flex: 1 1 auto;
    min-width: 0;
}

.note-meta {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 8px;
    margin-bottom: 4px;
}

.note-author {
    font-weight: 600;
    font-size: 13px;
    color: rgb(34, 34, 34);
}

.note-timestamp {
    font-size: 12px;
    color: rgb(146, 148, 156);
}

.note-category-pill {
    font-size: 11px;
    font-weight: 600;
    padding: 1px 8px;
    border-radius: 10px;
    white-space: nowrap;
}

.note-body {
    font-size: 14px;
    line-height: 1.5;
    color: rgb(34, 34, 34);
    white-space: pre-wrap;
    word-break: break-word;
    margin: 0;
}
</style>
